import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaRocket, FaGift, FaGem, FaGlobe, FaUsers, FaChartLine, FaTree, FaShieldAlt, FaExchangeAlt, FaCheckCircle, FaTrophy, FaFire } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import CountdownTimer from '../ui/CountdownTimer';
import { Link } from 'react-router-dom';
import bbtService from '../../services/bbtService';
import NewsletterForm from './NewsletterForm';

// Helper pour ajouter des mois à une date
const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

// Logique de persistance améliorée
const getPersistentTimestamp = (key, duration, unit) => {
  const storedTimestamp = localStorage.getItem(key);
  if (storedTimestamp) {
    const timestamp = parseInt(storedTimestamp, 10);
    // Si la date est dans le futur, on la garde
    if (timestamp > Date.now()) {
      return timestamp;
    }
    localStorage.removeItem(key);
  }

  let newDate;
  if (unit === 'months') {
    newDate = addMonths(new Date(), duration);
  } else { // 'days' par défaut
    const result = new Date();
    result.setDate(result.getDate() + duration);
    newDate = result;
  }
  
  const newTimestamp = newDate.getTime();
  localStorage.setItem(key, newTimestamp.toString());
  return newTimestamp;
};

// Données statiques pour optimiser les re-rendus
const FEATURES = [
  { 
    icon: <FaExchangeAlt className="text-green-400" />, 
    title: 'Trading P2P', 
    description: 'Transferts RUB ↔ XOF rapides et sécurisés',
    status: 'live'
  },
  { 
    icon: <FaUsers className="text-blue-400" />, 
    title: 'Staking BBT',
    description: 'Gagnez des récompenses en stakant vos BBT',
    status: 'soon'
  },
  { 
    icon: <FaShieldAlt className="text-purple-400" />, 
    title: 'Gouvernance DAO',
    description: 'Votez sur les décisions de l\'écosystème',
    status: 'soon'
  }
];

const REWARDS = [
  { icon: <FaGift className="text-green-400" />, text: '300 BBT bonus pour les nouveaux utilisateurs' },
  { icon: <FaGem className="text-blue-400" />, text: '50% réduction frais à vie pour les 1000 premiers' },
  { icon: <FaRocket className="text-purple-400" />, text: 'Accès VIP aux fonctionnalités Phase 2' }
];

const SOCIALS = [
  { icon: "twitter", url: "https://twitter.com/baobabexchange", label: "Twitter" },
  { icon: "telegram", url: "https://t.me/baobabexchange", label: "Telegram" },
  { icon: "discord", url: "https://discord.gg/baobabexchange", label: "Discord" }
];

const LaunchPromoPopup = ({ onClose }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [bbtStats, setBbtStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState(null);

  // Dates de lancement fixes et universelles pour un compte à rebours cohérent
  // Phase 1 : environ 30 jours
  const phase1LaunchTimestamp = new Date('2025-08-01T00:00:00Z').getTime();
  // Phase 2 : environ 60 jours
  const phase2LaunchTimestamp = new Date('2025-09-01T00:00:00Z').getTime();

  useEffect(() => {
    const checkVisibilityAndFetchData = async () => {
      // Afficher le pop-up si l'utilisateur ne l'a pas fermé dans les dernières 12 heures.
      const lastClosedTime = localStorage.getItem('launchPromoPopupClosed');
      const twelveHours = 12 * 60 * 60 * 1000;

      if (lastClosedTime && (Date.now() - parseInt(lastClosedTime, 10)) < twelveHours) {
        setIsVisible(false);
        return;
      }
      
      setIsVisible(true);

      // Charger les stats BBT
      try {
        setLoadingStats(true);
        setError(null);
        // Le service retourne { data: { ... } }
        const response = await bbtService.getBBTStats();
        setBbtStats(response.data || response);
      } catch (err) {
        console.error("Erreur lors du chargement des stats BBT pour le popup:", err);
        setError("Impossible de charger les statistiques.");
      } finally {
        setLoadingStats(false);
      }
    };

    // Un léger délai pour ne pas être trop intrusif au chargement de la page
    const timer = setTimeout(checkVisibilityAndFetchData, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // On enregistre le moment de la fermeture
    localStorage.setItem('launchPromoPopupClosed', Date.now().toString());
    if (onClose) onClose();
  };
  
  // Fermeture avec la touche Echap
  useEffect(() => {
    const handleEsc = (event) => {
       if (event.keyCode === 27) handleClose();
    };
    window.addEventListener('keydown', handleEsc);
    // On utilise la fonction de nettoyage pour retirer l'écouteur d'événement
    return () => window.removeEventListener('keydown', handleEsc);
  }, [handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderStat = (value, unit = '', defaultValue = 'N/A') => {
    if (loadingStats) return '...';
    if (error || value === null || value === undefined) return defaultValue;
    if (typeof value === 'number') {
      // Utilisation du formateur directement depuis le service en attendant de pouvoir corriger formatters.js
      return `${unit}${bbtService.formatTokenAmount(value, 2)}`;
    }
    return `${unit}${value}`;
  }

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-start justify-center z-[9999] p-4 pt-16 sm:pt-24 overflow-y-auto"
          onClick={handleClose}
        >
          <motion.div 
            initial={{ scale: 0.9, y: -50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: -50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 p-6 md:p-8 rounded-2xl shadow-2xl w-full max-w-4xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Effet de célébration */}
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-green-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
            
            {/* Bordure animée de succès */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 via-blue-500/30 to-yellow-500/30 animate-pulse-slow"></div>
            </div>
            
            {/* Bouton de fermeture */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 md:top-4 md:right-4 text-slate-400 hover:text-slate-100 transition-colors p-2 rounded-full hover:bg-slate-700/50 z-10"
              aria-label={t('common.close')}
            >
              <FaTimes size={20} />
            </button>

            <div className="relative z-10">
              {/* En-tête de célébration */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {/* Badge de succès */}
                  <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full text-lg font-bold mb-6 shadow-lg">
                    <FaCheckCircle className="mr-3 text-2xl" />
                    LANCEMENT RÉUSSI !
                  </div>
                  
                  <FaTrophy className="text-6xl md:text-7xl text-yellow-500 mx-auto mb-4" />
                  <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3 flex items-center justify-center gap-3">
                    <FaFire className="text-orange-500" /> BBT est maintenant LIVE ! <FaFire className="text-orange-500" />
                  </h2>
                  <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
                    {t('launchSuccess.subtitle', 'Le Baobab Token (BBT) a été lancé avec succès sur BSC Mainnet ! Découvrez dès maintenant toutes les fonctionnalités et préparez-vous pour la Phase 2.')}
                  </p>
                </motion.div>
              </div>

              {/* Statistiques de lancement en temps réel */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
              >
                <div className="bg-slate-800/50 p-4 rounded-xl border border-green-500/30 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {loadingStats ? '...' : (bbtStats?.marketCap > 0 ? `$${bbtService.formatTokenAmount(bbtStats.marketCap)}` : '$219K')}
                  </div>
                  <div className="text-sm text-slate-400">Market Cap</div>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-xl border border-blue-500/30 text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {loadingStats ? '...' : (bbtStats?.holders > 0 ? bbtStats.holders.toLocaleString() : '50+')}
                  </div>
                  <div className="text-sm text-slate-400">Holders</div>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-xl border border-purple-500/30 text-center">
                  <div className="text-2xl font-bold text-purple-400">
                    500M
                  </div>
                  <div className="text-sm text-slate-400">Supply Total</div>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-xl border border-orange-500/30 text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    <FaCheckCircle />
                  </div>
                  <div className="text-sm text-slate-400">Vérifié BSC</div>
                </div>
              </motion.div>

              {/* Prochaines étapes - Mise à jour des phases */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 p-5 rounded-xl border border-blue-500/30"
                >
                  <div className="flex items-center mb-3">
                    <FaFire className="text-blue-400 text-xl mr-2" />
                    <h3 className="text-lg font-bold text-white">Phase 1 : Staking & Expansion</h3>
                  </div>
                  <CountdownTimer 
                    targetDate={phase1LaunchTimestamp} 
                    title="Lancement de la Phase 1 dans :"
                    size="sm"
                  />
                  <p className="text-sm text-slate-300 mt-2">
                    Staking de BBT, listing sur d'autres DEX.
                  </p>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 p-5 rounded-xl border border-purple-500/30"
                >
                  <div className="flex items-center mb-3">
                    <FaGlobe className="text-purple-400 text-xl mr-2" />
                    <h3 className="text-lg font-bold text-white">Phase 2 : Gouvernance DAO</h3>
                  </div>
                  <CountdownTimer 
                    targetDate={phase2LaunchTimestamp} 
                    title="Lancement de la Phase 2 dans :"
                    size="sm"
                  />
                  <p className="text-sm text-slate-300 mt-2">
                    Votez sur les propositions de l'écosystème.
                  </p>
                </motion.div>
              </div>

              {/* Nouvelles fonctionnalités disponibles */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="mb-8"
              >
                <h3 className="text-xl font-bold mb-4 text-center text-white flex items-center justify-center">
                  <FaGift className="text-yellow-400 mr-2" />
                  Fonctionnalités de l'Écosystème
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {FEATURES.map((item, index) => (
                    <div key={index} className={`bg-slate-800/60 p-4 rounded-lg text-center border border-slate-700 relative overflow-hidden transition-opacity ${item.status === 'soon' ? 'opacity-60' : ''}`}>
                      {item.status === 'live' && (
                        <div className="absolute top-2 right-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-bold shadow-lg">
                          LIVE
                        </div>
                      )}
                      {item.status === 'soon' && (
                        <div className="absolute top-2 right-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-bold shadow-lg">
                          BIENTÔT
                        </div>
                      )}
                      <div className="text-3xl mx-auto mb-2 w-fit">{item.icon}</div>
                      <h4 className="font-bold text-white">{item.title}</h4>
                      <p className="text-xs text-slate-400">{item.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Récompenses early adopters */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="bg-gradient-to-r from-green-500/10 to-blue-500/10 p-6 rounded-xl border border-green-500/20 mb-8"
              >
                <h3 className="flex items-center text-lg font-semibold text-white mb-4">
                  <FaTrophy className="text-yellow-400 mr-2" />
                  Récompenses de Lancement - Dernière Chance !
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    {REWARDS.map((reward, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1 + index * 0.1 }}
                        className="flex items-start text-slate-300 text-sm gap-2"
                      >
                        <span className="mt-0.5">{reward.icon}</span>
                        <span>{reward.text}</span>
                      </motion.li>
                    ))}
                  </ul>
                  <div className="text-center">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-400">847/1000</div>
                      <div className="text-sm text-slate-400">Places VIP restantes</div>
                      <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                        <div className="bg-gradient-to-r from-green-400 to-blue-400 h-2 rounded-full" style={{width: '84.7%'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Newsletter - Collecte d'emails */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="mb-8"
              >
                <NewsletterForm variant="compact" source="popup" showRewards={false} className="max-w-md mx-auto" />
              </motion.div>

              {/* Actions principales */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
              >
                <Link 
                  to="/bbt" 
                  className="group relative px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-green-500/30 text-center"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <FaTree className="mr-2" />
                    Découvrir BBT
                  </span>
                </Link>
                
                <a 
                  href="https://pancakeswap.finance/swap?outputCurrency=0x0354a58290E141b241d5b32AE02D581289f286b7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-red-600 transform transition-all duration-200 hover:scale-105 text-center flex items-center justify-center"
                >
                  <FaRocket className="mr-2" />
                  Acheter sur PancakeSwap
                </a>
              </motion.div>

              {/* Footer */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="text-center"
              >
                <p className="text-xs text-slate-500 mb-2">
                  Contrat BBT vérifié sur BscScan : 0x0354a5...286b7
                </p>
                <div className="flex justify-center space-x-4">
                  {SOCIALS.map((social) => (
                    <a 
                      key={social.icon}
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-white transition-transform duration-300 transform hover:scale-110 p-2 rounded-full hover:bg-slate-700/50"
                      aria-label={social.label}
                    >
                      <img 
                        src={`/images/social/${social.icon}.svg`} 
                        alt={social.icon} 
                        className="w-6 h-6" // Icônes légèrement plus grandes
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/social/default.svg";
                        }}
                      />
                    </a>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <style jsx="true">{`
        @keyframes pulse-slow {
          0% { opacity: 0.3; }
          50% { opacity: 0.7; }
          100% { opacity: 0.3; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s infinite;
        }
      `}</style>
    </AnimatePresence>
  );
};

export default LaunchPromoPopup;
